<script setup lang="ts">
import type { EventVault } from "@/models"
import type { ToastHtmlElement } from "@/types"
import { useQueryClient } from "@tanstack/vue-query"
import { useTemplateRef } from "vue"
import { GET_EVENT_VAULT_QUERY_KEY, useCloseEvent } from "@/api"
import { BaseModal, Button, Card, Toast } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { CloseEventModalKey } from "@/symbols"

interface CloseEventModalProps {
  eventVault?: EventVault
  isLoading?: boolean
  drawerKey?: symbol
}

const props = withDefaults(defineProps<CloseEventModalProps>(), {
  eventVault: undefined,
  isLoading: false,
  drawerKey: CloseEventModalKey,
})

const { toggle } = useModal(props.drawerKey)
const { t } = useTranslation()

const successToast = useTemplateRef<ToastHtmlElement>("successToast")
const errorToast = useTemplateRef<ToastHtmlElement>("errorToast")

const queryClient = useQueryClient()
const { mutate, isPending } = useCloseEvent()

function onConfirm() {
  if (!props.eventVault?.id) {
    toggle()
    return
  }

  mutate(props.eventVault?.id, {
    async onSuccess() {
      successToast.value?.showToast({
        title: t("close_event_modal.success_title"),
        content: t("close_event_modal.success_content"),
        icon: "CircleCheck",
      })
      await queryClient.invalidateQueries({ queryKey: [GET_EVENT_VAULT_QUERY_KEY] })
      await queryClient.invalidateQueries({ queryKey: ["listEventVaults"] })
      toggle()
    },
    onError() {
      errorToast.value?.showToast()
    },
  })
}
</script>

<template>
  <BaseModal :name="drawerKey">
    <Card>
      <template #header>
        <div class="flex items-center justify-between">
          <h1 class="font-comfortaa font-bold text-2xl text-gray-900">
            {{ t('close_event_modal.title') }}
          </h1>
          <Button variant="flat" prefix-icon="Close" @click="toggle">
            <span class="sr-only">{{ t('close_event_modal.close') }}</span>
          </Button>
        </div>
      </template>
      <div v-if="isLoading || isPending" class="flex flex-col gap-4">
        <div class="gpx-skeleton w-full h-24" />
        <div class="gpx-skeleton w-full h-12" />
        <div class="gpx-skeleton w-full h-24" />
        <div class="flex items-center justify-end gap-3">
          <div class="gpx-skeleton w-24 h-12" />
          <div class="gpx-skeleton w-24 h-12" />
        </div>
      </div>
      <div v-else class="mt-3 text-gray-800">
        <p class="mb-1.5">
          {{ t('close_event_modal.description') }}
        </p>
        <ul class="list-disc ml-6 mb-8">
          <li>{{ t('close_event_modal.p1') }}</li>
          <li>{{ t('close_event_modal.p2') }}</li>
          <li>{{ t('close_event_modal.p3') }}</li>
        </ul>

        <div class="flex gap-3 items-center justify-end">
          <Button variant="tertiary" @click="toggle">
            {{ t('cancel') }}
          </Button>
          <Button variant="danger" @click="onConfirm">
            {{ t('confirm') }}
          </Button>
        </div>
      </div>
    </Card>
  </BaseModal>

  <Toast ref="successToast" variant="success" />
  <Toast ref="errorToast" variant="danger" />
</template>
